import type { CityConfig } from "../city-template/types";

export const chapelHillConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Chapel Hill",
  state: "North Carolina",
  stateAbbr: "NC",
  slug: "chapel-hill",
  route: "/cities/chapel-hill",
  title: "Emergency Passport Chapel Hill, NC — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Chapel Hill, NC — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Orange County and the Research Triangle. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Chapel Hill, NC",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Chapel Hill and Research Triangle travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Chapel%20Hill%20North%20Carolina%20campus%20town%20at%20golden%20hour%20with%20historic%20UNC%20buildings%20and%20the%20Old%20Well%20landmark%2C%20Franklin%20Street%20shops%20in%20the%20background%2C%20lush%20green%20trees%20and%20well-manicured%20lawns%2C%20warm%20amber%20sunset%20light%20over%20the%20Research%20Triangle%2C%20clean%20blue%20sky%20with%20soft%20clouds%2C%20professional%20cityscape%20photography%20with%20vibrant%20warm%20colors%20and%20academic%20charm%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=chapelhill-hero-nc-001&orientation=landscape",
  heroImageAlt: "Chapel Hill North Carolina with historic UNC campus buildings at golden hour",
  geoPosition: "35.9132;-79.0558",
  geoPlacename: "Chapel Hill, North Carolina",
  regionalAgency: {
    name: "Washington DC Passport Agency",
    address: "600 19th St NW, Washington, DC 20006",
    distance: "Approximately 275 miles north of Chapel Hill — about 4.5 hours via I-95",
  },
  nearbyCitiesZips: [
    { name: "Chapel Hill", zips: "27514–27517" },
    { name: "Durham", zips: "27701–27722" },
    { name: "Carrboro", zips: "27510" },
    { name: "Hillsborough", zips: "27278" },
    { name: "Cary", zips: "27511–27519" },
    { name: "Apex", zips: "27502, 27523, 27539" },
    { name: "Raleigh", zips: "27601–27699" },
    { name: "Morrisville", zips: "27560" },
    { name: "Pittsboro", zips: "27312" },
    { name: "Mebane", zips: "27302" },
    { name: "Burlington", zips: "27215, 27216, 27217" },
    { name: "Graham", zips: "27253" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Research Triangle departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "Research Triangle Hub", desc: "Serving Chapel Hill, Durham, Carrboro, and the entire Triangle area" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Durham, NC", route: "/cities/durham" },
    { name: "Fast passport renewal in Raleigh, NC", route: "/cities/raleigh" },
    { name: "Emergency passport help in Cary, NC", route: "/cities/cary" },
    { name: "Urgent passport services in Apex, NC", route: "/cities/apex" },
    { name: "Same-day passport help in Charlotte, NC", route: "/cities/charlotte" },
    { name: "Passport courier services in Winston-Salem, NC", route: "/cities/winston-salem" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Chapel Hill remotely via secure courier. We do not have a walk-in office in Chapel Hill — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Orange County and the Research Triangle area travelers.",
  fpcOfficeAddress: "301 Fayetteville St, Raleigh, NC 27601",
  mapEmbedUrl: "https://www.google.com/maps?q=301+Fayetteville+St,+Raleigh,+NC+27601&output=embed",
  fpcOfficeDistance: "Serving all of Orange County and the Research Triangle region",
  serviceAreaIntro: "From the UNC campus and Franklin Street to the quiet neighborhoods of Meadowmont and Southern Village, from Carrboro to Hillsborough — Fast Passport Center serves the entire Chapel Hill area. We regularly help travelers from Durham, Cary, Apex, and Raleigh get their passports fast.",
  nearbyCitiesIntro: "We serve all Chapel Hill neighborhoods and the surrounding Research Triangle including Durham, Carrboro, Hillsborough, Cary, Apex, Raleigh, Morrisville, Pittsboro, Mebane, Burlington, and Graham. If you are anywhere in the Triangle, we have you covered.",
  howItWorksIntro: "Most Chapel Hill travelers complete everything online and ship documents overnight. No need to drive to Washington DC or wait in line at the regional agency.",
  mistakesIntro: "Chapel Hill travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Chapel Hill and Orange County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Chapel Hill? Here are the most common ones we hear from Research Triangle travelers.",
  statePageRoute: "/states/north-carolina",
  statePageName: "North Carolina",
  dmvWebsite: "https://www.ncdot.gov",
  dmvLabel: "North Carolina DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Chapel Hill international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Dr. Sarah M.",
      location: "Chapel Hill, NC",
      rating: 5,
      text: "Conference in London in 3 days and my passport had expired. Fast Passport Center got my renewal ready in 48 hours. As a busy professor, I never had to leave town. Truly exceptional.",
      service: "Passport Renewal",
    },
    {
      name: "Kevin L.",
      location: "Carrboro, NC",
      rating: 5,
      text: "Emergency passport for a family emergency abroad. Got it in 36 hours. The team kept me updated every step. I was amazed this could happen without driving to Atlanta.",
      service: "Emergency Passport",
    },
    {
      name: "Rachel D.",
      location: "Durham, NC",
      rating: 5,
      text: "First passport for my 12-year-old before our study abroad program. Fast Passport Center walked us through every document and had her passport ready in 5 days. Completely stress-free.",
      service: "Child Passport",
    },
    {
      name: "Mark H.",
      location: "Hillsborough, NC",
      rating: 5,
      text: "Lost my passport 4 days before a research trip to Tokyo. Fast Passport Center had a replacement in my hands in 32 hours. These guys are absolutely lifesavers.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "chapelhill-1",
      name: "Chapel Hill Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "179 E Franklin St",
      city: "Chapel Hill",
      state: "NC",
      zip: "27514",
      phone: "(919) 968-6058",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Chapel Hill, UNC campus area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Chapel Hill facility. High volume — appointments strongly recommended. Call ahead to confirm passport acceptance.",
      icon: "ri-mail-line",
    },
    {
      id: "chapelhill-2",
      name: "Carrboro Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "103 Weaver St",
      city: "Carrboro",
      state: "NC",
      zip: "27510",
      phone: "(919) 929-0341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Adjacent to Chapel Hill",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Carrboro and west Chapel Hill residents. Less crowded than downtown. Photo services nearby.",
      icon: "ri-mail-line",
    },
    {
      id: "chapelhill-3",
      name: "Durham Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "323 E Chapel Hill St",
      city: "Durham",
      state: "NC",
      zip: "27701",
      phone: "(919) 687-7781",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "10 miles east of Chapel Hill",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major regional facility for Durham and Orange County. Ample parking. Appointments required.",
      icon: "ri-mail-line",
    },
    {
      id: "chapelhill-4",
      name: "Cary Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "315 E Chatham St",
      city: "Cary",
      state: "NC",
      zip: "27511",
      phone: "(919) 467-7581",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles southeast of Chapel Hill",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Wake County and eastern Triangle. Call for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "chapelhill-5",
      name: "Orange County Register of Deeds",
      type: "County Clerk Passport Acceptance",
      address: "200 S Cameron St",
      city: "Hillsborough",
      state: "NC",
      zip: "27278",
      phone: "(919) 245-2700",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Hillsborough, 10 miles north of Chapel Hill",
      website: "https://www.orangecountync.gov/",
      websiteLabel: "Orange County Register",
      notes: "Accepts passport applications. Good alternative to post office with extended hours. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "chapelhill-6",
      name: "Hillsborough Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "150 S Churton St",
      city: "Hillsborough",
      state: "NC",
      zip: "27278",
      phone: "(919) 732-2101",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "10 miles north of Chapel Hill",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves northern Orange County. Full passport services available.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 4.5 hour drive to Washington DC", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Chapel Hill photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Orange County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Chapel Hill applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Research Triangle families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Chapel Hill travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Chapel Hill." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Chapel Hill address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Chapel Hill before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Chapel Hill for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 4.5 hour drive to Washington DC." },
    { q: "How fast can I renew my passport in Chapel Hill?", a: "Fast Passport Center offers fast passport renewal in Chapel Hill in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Chapel Hill?", a: "The nearest regional passport agency to Chapel Hill is the Washington DC Passport Agency at 600 19th St NW, Washington DC 20006, approximately 275 miles north via I-95. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Chapel Hill without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Chapel Hill?", a: "Same-day passport appointments are only available at the Washington DC Passport Agency — approximately 4.5 hours from Chapel Hill — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Orange County residents." },
    { q: "Do you serve the entire Research Triangle?", a: "Absolutely. Fast Passport Center serves the entire Research Triangle including Chapel Hill, Durham, Carrboro, Hillsborough, Cary, Apex, Raleigh, Morrisville, and Pittsboro. Our courier service works seamlessly from any address in the Triangle region." },
    { q: "Can I expedite a child passport in Chapel Hill?", a: "Yes. Fast Passport Center expedites child passports in Chapel Hill for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Triangle families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Chapel Hill with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "Do I need to drive to the Washington DC Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Chapel Hill", address: "1315 Fordham Blvd, Chapel Hill, NC 27514", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walgreens — Chapel Hill", address: "1312 E Franklin St, Chapel Hill, NC 27514", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/storelocator" },
    { name: "Walmart Supercenter — Chapel Hill", address: "5910 Farrington Rd, Chapel Hill, NC 27517", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
    { name: "FedEx Office — Durham", address: "2609 Erwin Rd, Durham, NC 27705", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://local.fedex.com/en-us/nc/durham" },
    { name: "CVS Pharmacy — Durham", address: "1817 Martin Luther King Jr Pkwy, Durham, NC 27707", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walgreens — Cary", address: "1100 Kildaire Farm Rd, Cary, NC 27511", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/storelocator" },
    { name: "CVS Pharmacy — Hillsborough", address: "501 S Churton St, Hillsborough, NC 27278", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/store-locator" },
    { name: "Walmart — Carrboro", address: "1250 E Main St, Carrboro, NC 27510", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/store/finder" },
  ],
  dmvLocations: [
    {
      name: "NC DMV — Chapel Hill",
      address: "501 Weaver Dairy Rd, Chapel Hill, NC 27514",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "NC DMV — Durham",
      address: "3824 Guess Rd, Durham, NC 27705",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and passport-related ID services. Appointment strongly recommended.",
      featured: false,
    },
    {
      name: "NC DMV — Hillsborough",
      address: "1025 S Nash St, Hillsborough, NC 27278",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves northern Orange County residents. REAL ID available.",
      featured: false,
    },
    {
      name: "NC DMV — Cary",
      address: "600 E Chatham St, Cary, NC 27511",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Wake County and eastern Triangle. REAL ID available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "RDU",
      name: "Raleigh-Durham International Airport",
      distance: "20 miles east of Chapel Hill",
      address: "2400 John Brantley Blvd, Morrisville, NC 27560",
      note: "Primary airport for the Research Triangle. Direct flights to major domestic and international destinations including London, Paris, and Toronto.",
      flightUrl: "https://www.rdu.com/",
      flightLabel: "RDU Airport",
      featured: true,
    },
    {
      code: "GSO",
      name: "Piedmont Triad International Airport",
      distance: "60 miles west of Chapel Hill",
      address: "1000 Ted Johnson Pkwy, Greensboro, NC 27409",
      note: "Alternative airport for Piedmont Triad and western Triangle travelers. Additional domestic routes.",
      flightUrl: "https://www.flyfrompti.com/",
      flightLabel: "GSO Airport",
      featured: false,
    },
    {
      code: "CLT",
      name: "Charlotte Douglas International Airport",
      distance: "140 miles southwest of Chapel Hill",
      address: "5501 Josh Birmingham Pkwy, Charlotte, NC 28208",
      note: "Major international hub for long-haul international travel. American Airlines hub with direct flights to Europe, Mexico, and the Caribbean.",
      flightUrl: "https://www.cltairport.com/",
      flightLabel: "CLT Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: 'North Carolina Vital Records (NCDHHS)',
      description: 'Birth and death certificates for all NC counties. Order online, by mail, or in person at the Raleigh office.',
      url: 'https://vitalrecords.nc.gov/',
    },
    {
      label: 'Orange County Register of Deeds',
      description: 'Marriage licenses and records for Orange County. Apply in person or request certified copies.',
      url: 'https://www.orangecountync.gov/149/Register-of-Deeds',
    },
    {
      label: 'Orange County Clerk of Superior Court',
      description: 'Divorce records and decrees for Orange County. Contact the clerk for certified copies.',
      url: 'https://www.nccourts.gov/locations/orange-county/orange-county-clerk-of-superior-court',
    },
  ],
};